import React from 'react';
import { Box, Typography, Tooltip, Button } from '@mui/material';
import {
  CloudDone as SyncedIcon,
  PortableWifiOff as OfflineIcon,
  Sync as SavingIcon,
  ErrorOutlined as ErrorIcon,
  SignalCellularAlt as OnlineIcon,
} from '@mui/icons-material';
import { appColors } from '../../Config/Theme';
import type { SyncState } from '../../Hooks/useSyncStatus';

export interface SyncStatusIndicatorProps {
  state: SyncState;
  lastSyncedAt?: string | null;
  lastUpdatedAt?: string | null;
  errorMessage?: string | null;
  onRetry?: () => void;
  mode?: 'compact' | 'banner' | 'inline';
}

const formatSyncTime = (isoString: string | null | undefined): string => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
};

const stateConfig: Record<SyncState, {
  icon: React.ReactElement;
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
}> = {
  online: {
    icon: <OnlineIcon sx={{ fontSize: 16 }} />,
    label: 'Online',
    color: appColors.status.success,
    bgColor: appColors.status.successBg,
    borderColor: appColors.status.successBorder || appColors.status.success,
  },
  saving: {
    icon: <SavingIcon sx={{ fontSize: 16, animation: 'spin 1s linear infinite', '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />,
    label: 'Saving…',
    color: appColors.primary.main,
    bgColor: `${appColors.primary.main}14`,
    borderColor: appColors.primary.main,
  },
  synced: {
    icon: <SyncedIcon sx={{ fontSize: 16 }} />,
    label: 'Synced',
    color: appColors.status.success,
    bgColor: appColors.status.successBg,
    borderColor: appColors.status.successBorder || appColors.status.success,
  },
  offline: {
    icon: <OfflineIcon sx={{ fontSize: 16 }} />,
    label: 'Offline · Changes disabled',
    color: appColors.status.warning,
    bgColor: appColors.status.warningBg,
    borderColor: appColors.status.warning,
  },
  reconnecting: {
    icon: <SavingIcon sx={{ fontSize: 16, animation: 'spin 1s linear infinite', '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />,
    label: 'Reconnecting…',
    color: appColors.status.warning,
    bgColor: appColors.status.warningBg,
    borderColor: appColors.status.warning,
  },
  error: {
    icon: <ErrorIcon sx={{ fontSize: 16 }} />,
    label: 'Save failed',
    color: appColors.status.error,
    bgColor: '#FFEBEE',
    borderColor: appColors.status.error,
  },
};

const SyncStatusIndicator: React.FC<SyncStatusIndicatorProps> = ({
  state,
  lastSyncedAt,
  lastUpdatedAt,
  errorMessage,
  onRetry,
  mode = 'compact',
}) => {
  const cfg = stateConfig[state];

  // Build display text
  let displayText = cfg.label;
  if (state === 'online' && lastUpdatedAt) {
    const time = formatSyncTime(lastUpdatedAt);
    if (time) displayText = `Online \u00B7 Last updated ${time}`;
  } else if (state === 'synced' && lastSyncedAt) {
    const time = formatSyncTime(lastSyncedAt);
    if (time) displayText = `All changes saved ${time}`;
  }
  if (state === 'error' && errorMessage && errorMessage !== 'Save failed') {
    displayText = errorMessage;
  }

  // Tooltip text
  const tooltipText = state === 'offline'
    ? 'You are offline. Status updates are disabled until connection is restored.'
    : state === 'reconnecting'
      ? 'Reconnecting to realtime updates…'
      : state === 'error'
        ? 'The last save failed. Please retry.'
        : state === 'saving'
          ? 'Sending update to server…'
          : state === 'synced'
            ? 'All changes have been saved successfully.'
            : 'Connected to server.';

  if (mode === 'banner') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1,
          bgcolor: cfg.bgColor,
          border: `1px solid ${cfg.borderColor}`,
          borderRadius: '8px',
          width: '100%',
        }}
      >
        <Box sx={{ color: cfg.color, display: 'flex', alignItems: 'center' }}>{cfg.icon}</Box>
        <Typography variant="body2" sx={{ color: cfg.color, fontWeight: 600, flex: 1 }}>
          {displayText}
        </Typography>
        {state === 'error' && onRetry && (
          <Button
            size="small"
            onClick={onRetry}
            sx={{ color: cfg.color, fontWeight: 600, textTransform: 'none', minWidth: 'auto' }}
          >
            Retry
          </Button>
        )}
      </Box>
    );
  }

  // compact or inline
  return (
    <Tooltip title={tooltipText} arrow>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: mode === 'inline' ? 0 : 1.25,
          py: mode === 'inline' ? 0 : 0.5,
          bgcolor: mode === 'inline' ? 'transparent' : cfg.bgColor,
          border: mode === 'inline' ? 'none' : `1px solid ${cfg.borderColor}`,
          borderRadius: '16px',
          cursor: 'default',
          userSelect: 'none',
        }}
      >
        <Box sx={{ color: cfg.color, display: 'flex', alignItems: 'center', flexShrink: 0 }}>{cfg.icon}</Box>
        <Typography
          variant="caption"
          sx={{ color: cfg.color, fontWeight: 600, whiteSpace: 'nowrap', fontSize: '0.75rem' }}
        >
          {displayText}
        </Typography>
        {state === 'error' && onRetry && (
          <Button
            size="small"
            onClick={(e) => { e.stopPropagation(); onRetry(); }}
            sx={{
              color: cfg.color,
              fontWeight: 700,
              textTransform: 'none',
              minWidth: 'auto',
              fontSize: '0.72rem',
              px: 0.75,
              py: 0,
              ml: 0.25,
              textDecoration: 'underline',
              '&:hover': { textDecoration: 'underline', bgcolor: 'transparent' },
            }}
          >
            Retry
          </Button>
        )}
      </Box>
    </Tooltip>
  );
};

export default SyncStatusIndicator;
